import { Cvss4P0 } from 'ae-cvss-calculator';
import { type CvssScore } from '../cvss-score';
import { type CvssVectorParser } from '../cvss-vector-parser';
import {
    Cvss40SupplementalComponents,
    CvssVersion,
    type Cvss40SupplementalValues,
} from '../cvss-enums';
import { getCvssVersion } from '../utils/cvss-utils';
import { Cvss40Score } from './cvss40-score';

/**
 * Implements a service offering CVSS v4 vector parsing.
 *
 * @remarks
 * Consumers should be aware that {@link parse} will raise an exception if an invalid CVSS v4 vector string is passed.
 * This includes strings containing incorrect keys/values and those that are missing required entries.
 *
 * @public
 */
export class Cvss4VectorParser implements CvssVectorParser {
    /**
     * Parses a CVSS v4 vector string and converts it to a CVSS score.
     * @param vector - The CVSS v4 vector string.
     * @returns The CVSS score object.
     */

    public parse(vector: string): CvssScore {
        // TODO[CVSS]: Do something with those repetative checkings
        const version = getCvssVersion(vector);
        if (version !== CvssVersion.CVSS40) {
            throw new RangeError(
                `Invalid CVSS version: "${version}". Expected CVSS v4.0.`,
            );
        }

        try {
            const cvss4 = new Cvss4P0(vector);
            const scores = cvss4.calculateScores();

            const supplementalValues = {} as Cvss40SupplementalValues;
            for (const component of Cvss40SupplementalComponents) {
                supplementalValues[component] =
                    cvss4.getComponentByString(component).name;
            }

            return new Cvss40Score(
                scores.base,
                scores.environmental,
                scores.overall,
                scores.threat,
                supplementalValues,
            );
        } catch (error) {
            throw new Error(`Failed to parse CVSS v4 vector: ${vector}`);
        }
    }

    public reorderVector(vector: string): string {
        const version = getCvssVersion(vector);
        if (version !== CvssVersion.CVSS40) {
            throw new RangeError(
                `Invalid CVSS version: "${version}". Expected CVSS v4.0.`,
            );
        }

        try {
            const cvss4 = new Cvss4P0(vector);
            return cvss4.toString();
        } catch (error) {
            throw new Error(`Failed to reorder CVSS v4 vector: ${vector}`);
        }
    }
}
